import ListsModel from "../models/listsModel"
import store from "../store/store"

export default class CalculationFormulas {
    static maxWeightForTwoAxelTruck: number = 12000
    static maximumCo2Value: number = 400

    // käyttövoimavero pakettiauto, matkailuauto
    static powerTaxForVanOrCamperVan: number = 0.9
    static powerTaxForCamperVanPost2025: number = 5.5

    // Käyttövoimavero kuorma-autolle
    static TruckPowerTax(
        lists: ListsModel,
        axelAmount: string,
        trailerType: string,
        maxWeight: string = ""
    ): number {
        if (axelAmount === lists.AxelAmounts.twoAxel) {
            // Luvut jaetaan 100, jotta saadaan ne sentteinä
            if (trailerType === lists.TrailerTypes.noTrailer) {
                if (maxWeight.length > 0 && parseInt(maxWeight) > this.maxWeightForTwoAxelTruck) {
                    const erotus = parseInt(maxWeight) - this.maxWeightForTwoAxelTruck
                    const ylittavaMaara = 1.3 / 100 * Math.ceil(erotus / 100)
                    return (0.6 / 100 * this.maxWeightForTwoAxelTruck / 100) + ylittavaMaara
                } else {
                    return 0.6 / 100 * Math.ceil((parseInt(maxWeight) / 100))
                }
            } else if (trailerType === lists.TrailerTypes.semitrailer) {
                return 2.2 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            } else if (trailerType === lists.TrailerTypes.actualOrTwoAxelTrailer) {
                return 2.1 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            }
        } else if (axelAmount === lists.AxelAmounts.threeAxel) {
            if (trailerType === lists.TrailerTypes.noTrailer) {
                return 0.8 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            } else if (trailerType === lists.TrailerTypes.semitrailer) {
                return 1.3 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            } else if (trailerType === lists.TrailerTypes.actualOrTwoAxelTrailer) {
                return 1.4 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            }
        } else if (axelAmount === lists.AxelAmounts.fourAxel) {
            if (trailerType === lists.TrailerTypes.noTrailer) {
                return 0.7 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            } else if (trailerType === lists.TrailerTypes.semitrailer) {
                return 1.2 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            } else if (trailerType === lists.TrailerTypes.actualOrTwoAxelTrailer) {
                return 1.3 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            }
        } else if (axelAmount === lists.AxelAmounts.fiveAxel) {
            if (trailerType === lists.TrailerTypes.noTrailer) {
                return 0.6 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            } else if (trailerType === lists.TrailerTypes.semitrailer) {
                return 1.0 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            } else if (trailerType === lists.TrailerTypes.actualOrTwoAxelTrailer) {
                return 1.2 / 100 * Math.ceil((parseInt(maxWeight) / 100))
            }
        }
        return 0.000
    }

    // käyttövoimavero autolle ja kaksikäyttöautolle
    static PowerTaxForCarAndDualPurposeCar(
        lists: ListsModel,
        fuelType: string,
        vehicleWeight: number
    ): number {
        const weightFactor = Math.ceil(vehicleWeight / 100)
        switch (fuelType) {
            case lists.FuelTypes.diesel:
            case lists.FuelTypes.selfLoadingDieselHybrid:
                return 5.5 / 100 * weightFactor
            case lists.FuelTypes.electricity:
                return 1.5 / 100 * weightFactor
            case lists.FuelTypes.externallyChargeableGasolineHybrid:
                return 0.5 / 100 * weightFactor
            case lists.FuelTypes.externallyChargeableDieselHybrid:
                return 4.9 / 100 * weightFactor
            case lists.FuelTypes.methaneCNG:
            case lists.FuelTypes.gasolineCNG:
                return 3.1 / 100 * weightFactor
            default:
                return 0
        }
    }

    static PowerTaxForCarAndDualPurposeCarPost2025(
        lists: ListsModel,
        fuelType: string,
        vehicleWeight: number
    ): number {
        const weightFactor = Math.ceil(vehicleWeight / 100)
        switch (fuelType) {
            case lists.FuelTypes.diesel:
            case lists.FuelTypes.selfLoadingDieselHybrid:
                return 5.5 / 100 * weightFactor
            case lists.FuelTypes.electricity:
                return 1.9 / 100 * weightFactor
            case lists.FuelTypes.externallyChargeableGasolineHybrid:
                return 0.95 / 100 * weightFactor
            case lists.FuelTypes.externallyChargeableDieselHybrid:
                return 3.6 / 100 * weightFactor
            case lists.FuelTypes.methaneCNG:
            case lists.FuelTypes.gasolineCNG:
                return 3.1 / 100 * weightFactor
            default:
                return 0
        }
    }

    static BasicTaxForOtherVehicles(
        lists: ListsModel,
        selectedVehicle: string,
        year: number,
        mass: number,
        co2Type: string,
        co2Value: number
    ): { tax: number, calculatedWithMass: boolean } {
        return this.calculateBasicTaxForOtherVehicles(
            lists,
            selectedVehicle,
            year,
            mass,
            co2Type,
            co2Value,
            false
        )
    }

    static BasicTaxForOtherVehiclesPost2025(
        lists: ListsModel,
        selectedVehicle: string,
        year: number,
        mass: number,
        co2Type: string,
        co2Value: number
    ): { tax: number, calculatedWithMass: boolean } {
        return this.calculateBasicTaxForOtherVehicles(
            lists,
            selectedVehicle,
            year,
            mass,
            co2Type,
            co2Value,
            true
        )
    }

    // Perusvero auto, paketti-auto, kaksikäyttöauto, matkailuauto
    private static calculateBasicTaxForOtherVehicles(
        lists: ListsModel,
        selectedVehicle: string,
        year: number,
        mass: number,
        co2Type: string,
        co2Value: number,
        post2025: boolean
    ): { tax: number, calculatedWithMass: boolean } {
        const calculateWithMass = (mass: number) => post2025
            ? this.calculateBasicTaxWithMassPost2025(mass)
            : this.calculateBasicTaxWithMass(mass)

        const calculateWithNEDC = (co2Value: number) => post2025
            ? this.calculateBasicTaxWithNEDCPost2025(co2Value)
            : this.calculateBasicTaxWithNEDC(co2Value)

        const calculateWithWLTP = (co2Value: number) => post2025
            ? this.calculateBasicTaxWithWLTPPost2025(co2Value)
            : this.calculateBasicTaxWithWLTP(co2Value)

        const calculateTax = (massCondition: boolean, yearCondition: boolean) => {
            if (massCondition && yearCondition) {
                return {
                    tax: calculateWithMass(mass),
                    calculatedWithMass: true
                }
            }

            return {
                tax: co2Type === lists.CO2MeasureTypes.nedcEmission
                    ? calculateWithNEDC(co2Value)
                    : calculateWithWLTP(co2Value),
                calculatedWithMass: false
            }
        }

        switch (selectedVehicle) {
            case lists.VehicleTypes.car:
                return calculateTax(
                    (mass <= 2500 && year < 2001) || (mass > 2500 && year < 2002),
                    true
                )
            case lists.VehicleTypes.van:
                return calculateTax(true, year < 2008)
            case lists.VehicleTypes.dualPurposeCar:
                return calculateTax(true, year < 2006)
            case lists.VehicleTypes.camperVan:
                return calculateTax(false, false)
            default:
                return {
                    tax: 0,
                    calculatedWithMass: false
                }
        }
    }

    static calculateBasicTaxWithMass(value: number): number {
        // traficomin sivuilta, perusvero massan mukaan: https://www.traficom.fi/fi/liikenne/tieliikenne/ajoneuvoveron-rakenne-ja-maara
        if (value <= 1300) return 0.468
        if (value <= 1400) return 0.498
        if (value <= 1500) return 0.530
        if (value <= 1600) return 0.564
        if (value <= 1700) return 0.600
        if (value <= 1800) return 0.638
        if (value <= 1900) return 0.678
        if (value <= 2000) return 0.720
        if (value <= 2100) return 0.764
        if (value <= 2200) return 0.810
        if (value <= 2300) return 0.858
        if (value <= 2400) return 0.908
        if (value <= 2500) return 0.960
        if (value <= 2600) return 1.014
        if (value <= 2700) return 1.070
        if (value <= 2800) return 1.128
        if (value <= 2900) return 1.188
        if (value <= 3000) return 1.250
        if (value <= 3100) return 1.314
        if (value <= 3200) return 1.380
        if (value <= 3300) return 1.448
        if (value <= 3400) return 1.518
        return 1.590 // For values above 3400
    }

    static calculateBasicTaxWithMassPost2025(value: number): number {
        // For now same as pre-2025
        return this.calculateBasicTaxWithMass(value)
    }

    static calculateBasicTaxWithNEDC(co2Value: number): number {
        return this.calculateBasicTax(store.getters.nedcMap, co2Value)
    }

    static calculateBasicTaxWithNEDCPost2025(co2Value: number): number {
        return this.calculateBasicTax(store.getters.nedcMapPost2025, co2Value)
    }

    static calculateBasicTaxWithWLTP(co2Value: number): number {
        return this.calculateBasicTax(store.getters.wltpMap, co2Value)
    }

    static calculateBasicTaxWithWLTPPost2025(co2Value: number): number {
        return this.calculateBasicTax(store.getters.wltpMapPost2025, co2Value)
    }

    private static calculateBasicTax(
        taxMap: Map<number, number>,
        co2Value: number
    ): number {
        if (co2Value > this.maximumCo2Value) {
            return 654.44 / 365
        }

        const tax = taxMap.get(co2Value)
        return tax === undefined ? 0.000 : tax / 365
    }

    // populated in main.ts when app is starting
    static populateNedcMap(): Map<number, number> {
        const nedcMap: Map<number, number> = new Map()
        nedcMap.set(0, 53.29)
        nedcMap.set(1, 53.65)
        nedcMap.set(2, 54.02)
        nedcMap.set(3, 54.38)
        nedcMap.set(4, 54.75)
        nedcMap.set(5, 55.11)
        nedcMap.set(6, 55.48)
        nedcMap.set(7, 55.84)
        nedcMap.set(8, 56.21)
        nedcMap.set(9, 56.57)
        nedcMap.set(10, 57.30)
        nedcMap.set(11, 57.67)
        nedcMap.set(12, 58.03)
        nedcMap.set(13, 58.40)
        nedcMap.set(14, 58.76)
        nedcMap.set(15, 59.49)
        nedcMap.set(16, 59.86)
        nedcMap.set(17, 60.22)
        nedcMap.set(18, 60.59)
        nedcMap.set(19, 61.32)
        nedcMap.set(20, 61.68)
        nedcMap.set(21, 62.05)
        nedcMap.set(22, 62.78)
        nedcMap.set(23, 63.14)
        nedcMap.set(24, 63.51)
        nedcMap.set(25, 64.24)
        nedcMap.set(26, 64.60)
        nedcMap.set(27, 65.33)
        nedcMap.set(28, 65.70)
        nedcMap.set(29, 66.43)
        nedcMap.set(30, 66.79)
        nedcMap.set(31, 67.52)
        nedcMap.set(32, 67.89)
        nedcMap.set(33, 68.62)
        nedcMap.set(34, 68.98)
        nedcMap.set(35, 69.71)
        nedcMap.set(36, 70.08)
        nedcMap.set(37, 70.81)
        nedcMap.set(38, 71.54)
        nedcMap.set(39, 71.90)
        nedcMap.set(40, 72.63)
        nedcMap.set(41, 73.36)
        nedcMap.set(42, 73.73)
        nedcMap.set(43, 74.46)
        nedcMap.set(44, 75.19)
        nedcMap.set(45, 75.92)
        nedcMap.set(46, 76.28)
        nedcMap.set(47, 77.01)
        nedcMap.set(48, 77.74)
        nedcMap.set(49, 78.47)
        nedcMap.set(50, 79.20)
        nedcMap.set(51, 79.93)
        nedcMap.set(52, 80.66)
        nedcMap.set(53, 81.39)
        nedcMap.set(54, 81.76)
        nedcMap.set(55, 82.49)
        nedcMap.set(56, 83.58)
        nedcMap.set(57, 84.31)
        nedcMap.set(58, 85.04)
        nedcMap.set(59, 85.77)
        nedcMap.set(60, 86.50)
        nedcMap.set(61, 87.23)
        nedcMap.set(62, 87.96)
        nedcMap.set(63, 88.69)
        nedcMap.set(64, 89.42)
        nedcMap.set(65, 90.52)
        nedcMap.set(66, 91.25)
        nedcMap.set(67, 91.98)
        nedcMap.set(68, 93.07)
        nedcMap.set(69, 93.80)
        nedcMap.set(70, 94.53)
        nedcMap.set(71, 95.63)
        nedcMap.set(72, 96.36)
        nedcMap.set(73, 97.09)
        nedcMap.set(74, 98.18)
        nedcMap.set(75, 98.91)
        nedcMap.set(76, 100.01)
        nedcMap.set(77, 101.10)
        nedcMap.set(78, 101.83)
        nedcMap.set(79, 102.93)
        nedcMap.set(80, 103.66)
        nedcMap.set(81, 104.75)
        nedcMap.set(82, 105.85)
        nedcMap.set(83, 106.58)
        nedcMap.set(84, 107.67)
        nedcMap.set(85, 108.77)
        nedcMap.set(86, 109.86)
        nedcMap.set(87, 110.96)
        nedcMap.set(88, 112.05)
        nedcMap.set(89, 112.78)
        nedcMap.set(90, 113.88)
        nedcMap.set(91, 114.97)
        nedcMap.set(92, 116.07)
        nedcMap.set(93, 117.16)
        nedcMap.set(94, 118.26)
        nedcMap.set(95, 119.72)
        nedcMap.set(96, 120.81)
        nedcMap.set(97, 121.91)
        nedcMap.set(98, 123.00)
        nedcMap.set(99, 124.10)
        nedcMap.set(100, 125.56)
        nedcMap.set(101, 126.65)
        nedcMap.set(102, 127.75)
        nedcMap.set(103, 129.21)
        nedcMap.set(104, 130.30)
        nedcMap.set(105, 131.40)
        nedcMap.set(106, 132.86)
        nedcMap.set(107, 133.95)
        nedcMap.set(108, 135.41)
        nedcMap.set(109, 136.51)
        nedcMap.set(110, 137.97)
        nedcMap.set(111, 138.7)
        nedcMap.set(112, 139.795)
        nedcMap.set(113, 140.89)
        nedcMap.set(114, 141.62)
        nedcMap.set(115, 142.715)
        nedcMap.set(116, 143.81)
        nedcMap.set(117, 144.905)
        nedcMap.set(118, 145.635)
        nedcMap.set(119, 146.73)
        nedcMap.set(120, 147.825)
        nedcMap.set(121, 148.92)
        nedcMap.set(122, 149.65)
        nedcMap.set(123, 150.745)
        nedcMap.set(124, 151.84)
        nedcMap.set(125, 152.935)
        nedcMap.set(126, 153.665)
        nedcMap.set(127, 154.76)
        nedcMap.set(128, 155.855)
        nedcMap.set(129, 156.95)
        nedcMap.set(130, 157.68)
        nedcMap.set(131, 158.775)
        nedcMap.set(132, 159.87)
        nedcMap.set(133, 160.965)
        nedcMap.set(134, 161.695)
        nedcMap.set(135, 162.79)
        nedcMap.set(136, 163.885)
        nedcMap.set(137, 164.615)
        nedcMap.set(138, 165.71)
        nedcMap.set(139, 166.805)
        nedcMap.set(140, 167.9)
        nedcMap.set(141, 168.63)
        nedcMap.set(142, 169.725)
        nedcMap.set(143, 170.82)
        nedcMap.set(144, 171.915)
        nedcMap.set(145, 172.645)
        nedcMap.set(146, 173.74)
        nedcMap.set(147, 174.835)
        nedcMap.set(148, 175.93)
        nedcMap.set(149, 176.66)
        nedcMap.set(150, 177.755)
        nedcMap.set(151, 178.85)
        nedcMap.set(152, 179.945)
        nedcMap.set(153, 180.675)
        nedcMap.set(154, 181.77)
        nedcMap.set(155, 182.865)
        nedcMap.set(156, 183.96)
        nedcMap.set(157, 184.69)
        nedcMap.set(158, 185.785)
        nedcMap.set(159, 186.88)
        nedcMap.set(160, 187.61)
        nedcMap.set(161, 188.705)
        nedcMap.set(162, 189.8)
        nedcMap.set(163, 190.895)
        nedcMap.set(164, 191.625)
        nedcMap.set(165, 192.72)
        nedcMap.set(166, 193.815)
        nedcMap.set(167, 194.91)
        nedcMap.set(168, 195.64)
        nedcMap.set(169, 196.735)
        nedcMap.set(170, 197.83)
        nedcMap.set(171, 198.925)
        nedcMap.set(172, 199.655)
        nedcMap.set(173, 200.75)
        nedcMap.set(174, 201.845)
        nedcMap.set(175, 202.94)
        nedcMap.set(176, 206.59)
        nedcMap.set(177, 210.24)
        nedcMap.set(178, 214.255)
        nedcMap.set(179, 217.905)
        nedcMap.set(180, 221.92)
        nedcMap.set(181, 225.57)
        nedcMap.set(182, 229.22)
        nedcMap.set(183, 233.235)
        nedcMap.set(184, 236.885)
        nedcMap.set(185, 240.9)
        nedcMap.set(186, 244.55)
        nedcMap.set(187, 248.2)
        nedcMap.set(188, 252.215)
        nedcMap.set(189, 255.865)
        nedcMap.set(190, 259.88)
        nedcMap.set(191, 263.53)
        nedcMap.set(192, 267.18)
        nedcMap.set(193, 271.195)
        nedcMap.set(194, 274.845)
        nedcMap.set(195, 278.86)
        nedcMap.set(196, 282.51)
        nedcMap.set(197, 286.525)
        nedcMap.set(198, 290.175)
        nedcMap.set(199, 293.825)
        nedcMap.set(200, 297.84)
        nedcMap.set(201, 301.49)
        nedcMap.set(202, 305.505)
        nedcMap.set(203, 309.155)
        nedcMap.set(204, 312.805)
        nedcMap.set(205, 316.82)
        nedcMap.set(206, 320.47)
        nedcMap.set(207, 324.485)
        nedcMap.set(208, 328.135)
        nedcMap.set(209, 331.785)
        nedcMap.set(210, 335.8)
        nedcMap.set(211, 339.45)
        nedcMap.set(212, 341.27)
        nedcMap.set(213, 343.46)
        nedcMap.set(214, 345.65)
        nedcMap.set(215, 347.84)
        nedcMap.set(216, 350.03)
        nedcMap.set(217, 352.22)
        nedcMap.set(218, 354.41)
        nedcMap.set(219, 356.60)
        nedcMap.set(220, 358.79)
        nedcMap.set(221, 360.98)
        nedcMap.set(222, 363.17)
        nedcMap.set(223, 365.36)
        nedcMap.set(224, 367.55)
        nedcMap.set(225, 369.74)
        nedcMap.set(226, 371.93)
        nedcMap.set(227, 374.12)
        nedcMap.set(228, 376.31)
        nedcMap.set(229, 378.50)
        nedcMap.set(230, 380.69)
        nedcMap.set(231, 382.88)
        nedcMap.set(232, 385.07)
        nedcMap.set(233, 387.63)
        nedcMap.set(234, 389.82)
        nedcMap.set(235, 392.01)
        nedcMap.set(236, 394.20)
        nedcMap.set(237, 396.39)
        nedcMap.set(238, 398.58)
        nedcMap.set(239, 400.77)
        nedcMap.set(240, 402.96)
        nedcMap.set(241, 405.15)
        nedcMap.set(242, 407.34)
        nedcMap.set(243, 409.53)
        nedcMap.set(244, 412.08)
        nedcMap.set(245, 414.27)
        nedcMap.set(246, 416.46)
        nedcMap.set(247, 418.65)
        nedcMap.set(248, 420.84)
        nedcMap.set(249, 423.03)
        nedcMap.set(250, 425.22)
        nedcMap.set(251, 427.41)
        nedcMap.set(252, 429.60)
        nedcMap.set(253, 431.79)
        nedcMap.set(254, 433.98)
        nedcMap.set(255, 436.17)
        nedcMap.set(256, 438.36)
        nedcMap.set(257, 440.55)
        nedcMap.set(258, 442.74)
        nedcMap.set(259, 444.93)
        nedcMap.set(260, 447.12)
        nedcMap.set(261, 449.31)
        nedcMap.set(262, 451.50)
        nedcMap.set(263, 453.69)
        nedcMap.set(264, 455.88)
        nedcMap.set(265, 457.71)
        nedcMap.set(266, 459.90)
        nedcMap.set(267, 462.09)
        nedcMap.set(268, 464.28)
        nedcMap.set(269, 466.47)
        nedcMap.set(270, 468.66)
        nedcMap.set(271, 470.48)
        nedcMap.set(272, 472.67)
        nedcMap.set(273, 474.86)
        nedcMap.set(274, 477.05)
        nedcMap.set(275, 478.88)
        nedcMap.set(276, 481.07)
        nedcMap.set(277, 483.26)
        nedcMap.set(278, 485.08)
        nedcMap.set(279, 487.27)
        nedcMap.set(280, 489.10)
        nedcMap.set(281, 491.29)
        nedcMap.set(282, 493.11)
        nedcMap.set(283, 495.30)
        nedcMap.set(284, 497.49)
        nedcMap.set(285, 499.32)
        nedcMap.set(286, 501.14)
        nedcMap.set(287, 503.33)
        nedcMap.set(288, 505.16)
        nedcMap.set(289, 507.35)
        nedcMap.set(290, 509.17)
        nedcMap.set(291, 511.00)
        nedcMap.set(292, 513.19)
        nedcMap.set(293, 515.01)
        nedcMap.set(294, 516.84)
        nedcMap.set(295, 518.66)
        nedcMap.set(296, 520.49)
        nedcMap.set(297, 522.68)
        nedcMap.set(298, 524.50)
        nedcMap.set(299, 526.33)
        nedcMap.set(300, 528.15)
        nedcMap.set(301, 529.98)
        nedcMap.set(302, 531.80)
        nedcMap.set(303, 533.63)
        nedcMap.set(304, 535.45)
        nedcMap.set(305, 537.28)
        nedcMap.set(306, 539.10)
        nedcMap.set(307, 540.56)
        nedcMap.set(308, 542.39)
        nedcMap.set(309, 544.21)
        nedcMap.set(310, 546.04)
        nedcMap.set(311, 547.86)
        nedcMap.set(312, 549.32)
        nedcMap.set(313, 551.15)
        nedcMap.set(314, 552.61)
        nedcMap.set(315, 554.43)
        nedcMap.set(316, 556.26)
        nedcMap.set(317, 557.72)
        nedcMap.set(318, 559.54)
        nedcMap.set(319, 561.00)
        nedcMap.set(320, 562.83)
        nedcMap.set(321, 564.29)
        nedcMap.set(322, 565.75)
        nedcMap.set(323, 567.57)
        nedcMap.set(324, 569.03)
        nedcMap.set(325, 570.49)
        nedcMap.set(326, 571.95)
        nedcMap.set(327, 573.78)
        nedcMap.set(328, 575.24)
        nedcMap.set(329, 576.70)
        nedcMap.set(330, 578.16)
        nedcMap.set(331, 579.62)
        nedcMap.set(332, 581.08)
        nedcMap.set(333, 582.54)
        nedcMap.set(334, 584.00)
        nedcMap.set(335, 585.46)
        nedcMap.set(336, 586.92)
        nedcMap.set(337, 588.38)
        nedcMap.set(338, 589.84)
        nedcMap.set(339, 590.93)
        nedcMap.set(340, 592.39)
        nedcMap.set(341, 593.85)
        nedcMap.set(342, 595.31)
        nedcMap.set(343, 596.41)
        nedcMap.set(344, 597.87)
        nedcMap.set(345, 599.33)
        nedcMap.set(346, 600.42)
        nedcMap.set(347, 601.88)
        nedcMap.set(348, 602.98)
        nedcMap.set(349, 604.44)
        nedcMap.set(350, 605.53)
        nedcMap.set(351, 606.99)
        nedcMap.set(352, 608.09)
        nedcMap.set(353, 609.18)
        nedcMap.set(354, 610.64)
        nedcMap.set(355, 611.74)
        nedcMap.set(356, 612.83)
        nedcMap.set(357, 613.93)
        nedcMap.set(358, 615.02)
        nedcMap.set(359, 616.48)
        nedcMap.set(360, 617.58)
        nedcMap.set(361, 618.67)
        nedcMap.set(362, 619.77)
        nedcMap.set(363, 620.86)
        nedcMap.set(364, 621.96)
        nedcMap.set(365, 623.05)
        nedcMap.set(366, 624.15)
        nedcMap.set(367, 625.24)
        nedcMap.set(368, 626.34)
        nedcMap.set(369, 627.43)
        nedcMap.set(370, 628.16)
        nedcMap.set(371, 629.26)
        nedcMap.set(372, 630.35)
        nedcMap.set(373, 631.45)
        nedcMap.set(374, 632.18)
        nedcMap.set(375, 633.27)
        nedcMap.set(376, 634.37)
        nedcMap.set(377, 635.10)
        nedcMap.set(378, 636.19)
        nedcMap.set(379, 636.92)
        nedcMap.set(380, 638.02)
        nedcMap.set(381, 639.11)
        nedcMap.set(382, 639.84)
        nedcMap.set(383, 640.57)
        nedcMap.set(384, 641.67)
        nedcMap.set(385, 642.40)
        nedcMap.set(386, 643.49)
        nedcMap.set(387, 644.22)
        nedcMap.set(388, 644.95)
        nedcMap.set(389, 646.05)
        nedcMap.set(390, 646.78)
        nedcMap.set(391, 647.51)
        nedcMap.set(392, 648.24)
        nedcMap.set(393, 649.33)
        nedcMap.set(394, 650.06)
        nedcMap.set(395, 650.79)
        nedcMap.set(396, 651.52)
        nedcMap.set(397, 652.25)
        nedcMap.set(398, 652.98)
        nedcMap.set(399, 653.71)
        nedcMap.set(400, 654.44)
        return nedcMap
    }

    static populateNedcMapPost2025(): Map<number, number> {
        const nedcMap: Map<number, number> = new Map()
        for (let i = 0; i <= 82; i++) {
            nedcMap.set(i, 106.21)
        }
        nedcMap.set(83, 106.58)
        nedcMap.set(84, 107.67)
        nedcMap.set(85, 108.77)
        nedcMap.set(86, 109.86)
        nedcMap.set(87, 110.96)
        nedcMap.set(88, 112.05)
        nedcMap.set(89, 112.78)
        nedcMap.set(90, 113.88)
        nedcMap.set(91, 114.97)
        nedcMap.set(92, 116.07)
        nedcMap.set(93, 117.16)
        nedcMap.set(94, 118.26)
        nedcMap.set(95, 119.72)
        nedcMap.set(96, 120.81)
        nedcMap.set(97, 121.91)
        nedcMap.set(98, 123.00)
        nedcMap.set(99, 124.10)
        nedcMap.set(100, 125.56)
        nedcMap.set(101, 126.65)
        nedcMap.set(102, 127.75)
        nedcMap.set(103, 129.21)
        nedcMap.set(104, 130.30)
        nedcMap.set(105, 131.40)
        nedcMap.set(106, 132.86)
        nedcMap.set(107, 133.95)
        nedcMap.set(108, 135.41)
        nedcMap.set(109, 136.51)
        nedcMap.set(110, 137.97)
        nedcMap.set(111, 138.7)
        nedcMap.set(112, 139.795)
        nedcMap.set(113, 140.89)
        nedcMap.set(114, 141.62)
        nedcMap.set(115, 142.715)
        nedcMap.set(116, 143.81)
        nedcMap.set(117, 144.905)
        nedcMap.set(118, 145.635)
        nedcMap.set(119, 146.73)
        nedcMap.set(120, 147.825)
        nedcMap.set(121, 148.92)
        nedcMap.set(122, 149.65)
        nedcMap.set(123, 150.745)
        nedcMap.set(124, 151.84)
        nedcMap.set(125, 152.935)
        nedcMap.set(126, 153.665)
        nedcMap.set(127, 154.76)
        nedcMap.set(128, 155.855)
        nedcMap.set(129, 156.95)
        nedcMap.set(130, 157.68)
        nedcMap.set(131, 158.775)
        nedcMap.set(132, 159.87)
        nedcMap.set(133, 160.965)
        nedcMap.set(134, 161.695)
        nedcMap.set(135, 162.79)
        nedcMap.set(136, 163.885)
        nedcMap.set(137, 164.615)
        nedcMap.set(138, 165.71)
        nedcMap.set(139, 166.805)
        nedcMap.set(140, 167.9)
        nedcMap.set(141, 168.63)
        nedcMap.set(142, 169.725)
        nedcMap.set(143, 170.82)
        nedcMap.set(144, 171.915)
        nedcMap.set(145, 172.645)
        nedcMap.set(146, 173.74)
        nedcMap.set(147, 174.835)
        nedcMap.set(148, 175.93)
        nedcMap.set(149, 176.66)
        nedcMap.set(150, 177.755)
        nedcMap.set(151, 178.85)
        nedcMap.set(152, 179.945)
        nedcMap.set(153, 180.675)
        nedcMap.set(154, 181.77)
        nedcMap.set(155, 182.865)
        nedcMap.set(156, 183.96)
        nedcMap.set(157, 184.69)
        nedcMap.set(158, 185.785)
        nedcMap.set(159, 186.88)
        nedcMap.set(160, 187.61)
        nedcMap.set(161, 188.705)
        nedcMap.set(162, 189.8)
        nedcMap.set(163, 190.895)
        nedcMap.set(164, 191.625)
        nedcMap.set(165, 192.72)
        nedcMap.set(166, 193.815)
        nedcMap.set(167, 194.91)
        nedcMap.set(168, 195.64)
        nedcMap.set(169, 196.735)
        nedcMap.set(170, 197.83)
        nedcMap.set(171, 198.925)
        nedcMap.set(172, 199.655)
        nedcMap.set(173, 200.75)
        nedcMap.set(174, 201.845)
        nedcMap.set(175, 202.94)
        nedcMap.set(176, 206.59)
        nedcMap.set(177, 210.24)
        nedcMap.set(178, 214.255)
        nedcMap.set(179, 217.905)
        nedcMap.set(180, 221.92)
        nedcMap.set(181, 225.57)
        nedcMap.set(182, 229.22)
        nedcMap.set(183, 233.235)
        nedcMap.set(184, 236.885)
        nedcMap.set(185, 240.9)
        nedcMap.set(186, 244.55)
        nedcMap.set(187, 248.2)
        nedcMap.set(188, 252.215)
        nedcMap.set(189, 255.865)
        nedcMap.set(190, 259.88)
        nedcMap.set(191, 263.53)
        nedcMap.set(192, 267.18)
        nedcMap.set(193, 271.195)
        nedcMap.set(194, 274.845)
        nedcMap.set(195, 278.86)
        nedcMap.set(196, 282.51)
        nedcMap.set(197, 286.525)
        nedcMap.set(198, 290.175)
        nedcMap.set(199, 293.825)
        nedcMap.set(200, 297.84)
        nedcMap.set(201, 301.49)
        nedcMap.set(202, 305.505)
        nedcMap.set(203, 309.155)
        nedcMap.set(204, 312.805)
        nedcMap.set(205, 316.82)
        nedcMap.set(206, 320.47)
        nedcMap.set(207, 324.485)
        nedcMap.set(208, 328.135)
        nedcMap.set(209, 331.785)
        nedcMap.set(210, 335.8)
        nedcMap.set(211, 339.45)
        nedcMap.set(212, 341.27)
        nedcMap.set(213, 343.46)
        nedcMap.set(214, 345.65)
        nedcMap.set(215, 347.84)
        nedcMap.set(216, 350.03)
        nedcMap.set(217, 352.22)
        nedcMap.set(218, 354.41)
        nedcMap.set(219, 356.60)
        nedcMap.set(220, 358.79)
        nedcMap.set(221, 360.98)
        nedcMap.set(222, 363.17)
        nedcMap.set(223, 365.36)
        nedcMap.set(224, 367.55)
        nedcMap.set(225, 369.74)
        nedcMap.set(226, 371.93)
        nedcMap.set(227, 374.12)
        nedcMap.set(228, 376.31)
        nedcMap.set(229, 378.50)
        nedcMap.set(230, 380.69)
        nedcMap.set(231, 382.88)
        nedcMap.set(232, 385.07)
        nedcMap.set(233, 387.63)
        nedcMap.set(234, 389.82)
        nedcMap.set(235, 392.01)
        nedcMap.set(236, 394.20)
        nedcMap.set(237, 396.39)
        nedcMap.set(238, 398.58)
        nedcMap.set(239, 400.77)
        nedcMap.set(240, 402.96)
        nedcMap.set(241, 405.15)
        nedcMap.set(242, 407.34)
        nedcMap.set(243, 409.53)
        nedcMap.set(244, 412.08)
        nedcMap.set(245, 414.27)
        nedcMap.set(246, 416.46)
        nedcMap.set(247, 418.65)
        nedcMap.set(248, 420.84)
        nedcMap.set(249, 423.03)
        nedcMap.set(250, 425.22)
        nedcMap.set(251, 427.41)
        nedcMap.set(252, 429.60)
        nedcMap.set(253, 431.79)
        nedcMap.set(254, 433.98)
        nedcMap.set(255, 436.17)
        nedcMap.set(256, 438.36)
        nedcMap.set(257, 440.55)
        nedcMap.set(258, 442.74)
        nedcMap.set(259, 444.93)
        nedcMap.set(260, 447.12)
        nedcMap.set(261, 449.31)
        nedcMap.set(262, 451.50)
        nedcMap.set(263, 453.69)
        nedcMap.set(264, 455.88)
        nedcMap.set(265, 457.71)
        nedcMap.set(266, 459.90)
        nedcMap.set(267, 462.09)
        nedcMap.set(268, 464.28)
        nedcMap.set(269, 466.47)
        nedcMap.set(270, 468.66)
        nedcMap.set(271, 470.48)
        nedcMap.set(272, 472.67)
        nedcMap.set(273, 474.86)
        nedcMap.set(274, 477.05)
        nedcMap.set(275, 478.88)
        nedcMap.set(276, 481.07)
        nedcMap.set(277, 483.26)
        nedcMap.set(278, 485.08)
        nedcMap.set(279, 487.27)
        nedcMap.set(280, 489.10)
        nedcMap.set(281, 491.29)
        nedcMap.set(282, 493.11)
        nedcMap.set(283, 495.30)
        nedcMap.set(284, 497.49)
        nedcMap.set(285, 499.32)
        nedcMap.set(286, 501.14)
        nedcMap.set(287, 503.33)
        nedcMap.set(288, 505.16)
        nedcMap.set(289, 507.35)
        nedcMap.set(290, 509.17)
        nedcMap.set(291, 511.00)
        nedcMap.set(292, 513.19)
        nedcMap.set(293, 515.01)
        nedcMap.set(294, 516.84)
        nedcMap.set(295, 518.66)
        nedcMap.set(296, 520.49)
        nedcMap.set(297, 522.68)
        nedcMap.set(298, 524.50)
        nedcMap.set(299, 526.33)
        nedcMap.set(300, 528.15)
        nedcMap.set(301, 529.98)
        nedcMap.set(302, 531.80)
        nedcMap.set(303, 533.63)
        nedcMap.set(304, 535.45)
        nedcMap.set(305, 537.28)
        nedcMap.set(306, 539.10)
        nedcMap.set(307, 540.56)
        nedcMap.set(308, 542.39)
        nedcMap.set(309, 544.21)
        nedcMap.set(310, 546.04)
        nedcMap.set(311, 547.86)
        nedcMap.set(312, 549.32)
        nedcMap.set(313, 551.15)
        nedcMap.set(314, 552.61)
        nedcMap.set(315, 554.43)
        nedcMap.set(316, 556.26)
        nedcMap.set(317, 557.72)
        nedcMap.set(318, 559.54)
        nedcMap.set(319, 561.00)
        nedcMap.set(320, 562.83)
        nedcMap.set(321, 564.29)
        nedcMap.set(322, 565.75)
        nedcMap.set(323, 567.57)
        nedcMap.set(324, 569.03)
        nedcMap.set(325, 570.49)
        nedcMap.set(326, 571.95)
        nedcMap.set(327, 573.78)
        nedcMap.set(328, 575.24)
        nedcMap.set(329, 576.70)
        nedcMap.set(330, 578.16)
        nedcMap.set(331, 579.62)
        nedcMap.set(332, 581.08)
        nedcMap.set(333, 582.54)
        nedcMap.set(334, 584.00)
        nedcMap.set(335, 585.46)
        nedcMap.set(336, 586.92)
        nedcMap.set(337, 588.38)
        nedcMap.set(338, 589.84)
        nedcMap.set(339, 590.93)
        nedcMap.set(340, 592.39)
        nedcMap.set(341, 593.85)
        nedcMap.set(342, 595.31)
        nedcMap.set(343, 596.41)
        nedcMap.set(344, 597.87)
        nedcMap.set(345, 599.33)
        nedcMap.set(346, 600.42)
        nedcMap.set(347, 601.88)
        nedcMap.set(348, 602.98)
        nedcMap.set(349, 604.44)
        nedcMap.set(350, 605.53)
        nedcMap.set(351, 606.99)
        nedcMap.set(352, 608.09)
        nedcMap.set(353, 609.18)
        nedcMap.set(354, 610.64)
        nedcMap.set(355, 611.74)
        nedcMap.set(356, 612.83)
        nedcMap.set(357, 613.93)
        nedcMap.set(358, 615.02)
        nedcMap.set(359, 616.48)
        nedcMap.set(360, 617.58)
        nedcMap.set(361, 618.67)
        nedcMap.set(362, 619.77)
        nedcMap.set(363, 620.86)
        nedcMap.set(364, 621.96)
        nedcMap.set(365, 623.05)
        nedcMap.set(366, 624.15)
        nedcMap.set(367, 625.24)
        nedcMap.set(368, 626.34)
        nedcMap.set(369, 627.43)
        nedcMap.set(370, 628.16)
        nedcMap.set(371, 629.26)
        nedcMap.set(372, 630.35)
        nedcMap.set(373, 631.45)
        nedcMap.set(374, 632.18)
        nedcMap.set(375, 633.27)
        nedcMap.set(376, 634.37)
        nedcMap.set(377, 635.10)
        nedcMap.set(378, 636.19)
        nedcMap.set(379, 636.92)
        nedcMap.set(380, 638.02)
        nedcMap.set(381, 639.11)
        nedcMap.set(382, 639.84)
        nedcMap.set(383, 640.57)
        nedcMap.set(384, 641.67)
        nedcMap.set(385, 642.40)
        nedcMap.set(386, 643.49)
        nedcMap.set(387, 644.22)
        nedcMap.set(388, 644.95)
        nedcMap.set(389, 646.05)
        nedcMap.set(390, 646.78)
        nedcMap.set(391, 647.51)
        nedcMap.set(392, 648.24)
        nedcMap.set(393, 649.33)
        nedcMap.set(394, 650.06)
        nedcMap.set(395, 650.79)
        nedcMap.set(396, 651.52)
        nedcMap.set(397, 652.25)
        nedcMap.set(398, 652.98)
        nedcMap.set(399, 653.71)
        nedcMap.set(400, 654.44)
        return nedcMap
    }

    // populated in main.ts when app is starting
    static populateWltpMap(): Map<number, number> {
        // Tiedot kopioitu notepad++ ja siitä nopeasti modattu map formaattiin. https://www.traficom.fi/fi/liikenne/tieliikenne/ajoneuvoveron-rakenne-ja-maara
        const wltpMap: Map<number, number> = new Map()
        wltpMap.set(0, 53.29)
        wltpMap.set(1, 53.29)
        wltpMap.set(2, 53.65)
        wltpMap.set(3, 54.02)
        wltpMap.set(4, 54.38)
        wltpMap.set(5, 54.75)
        wltpMap.set(6, 55.11)
        wltpMap.set(7, 55.48)
        wltpMap.set(8, 55.48)
        wltpMap.set(9, 55.84)
        wltpMap.set(10, 56.21)
        wltpMap.set(11, 56.57)
        wltpMap.set(12, 56.94)
        wltpMap.set(13, 57.30)
        wltpMap.set(14, 57.67)
        wltpMap.set(15, 58.03)
        wltpMap.set(16, 58.40)
        wltpMap.set(17, 58.76)
        wltpMap.set(18, 59.13)
        wltpMap.set(19, 59.49)
        wltpMap.set(20, 59.86)
        wltpMap.set(21, 60.22)
        wltpMap.set(22, 60.59)
        wltpMap.set(23, 60.95)
        wltpMap.set(24, 61.32)
        wltpMap.set(25, 61.68)
        wltpMap.set(26, 62.05)
        wltpMap.set(27, 62.41)
        wltpMap.set(28, 62.78)
        wltpMap.set(29, 63.14)
        wltpMap.set(30, 63.51)
        wltpMap.set(31, 64.24)
        wltpMap.set(32, 64.60)
        wltpMap.set(33, 64.97)
        wltpMap.set(34, 65.33)
        wltpMap.set(35, 65.70)
        wltpMap.set(36, 66.06)
        wltpMap.set(37, 66.43)
        wltpMap.set(38, 67.16)
        wltpMap.set(39, 67.52)
        wltpMap.set(40, 67.89)
        wltpMap.set(41, 68.25)
        wltpMap.set(42, 68.98)
        wltpMap.set(43, 69.35)
        wltpMap.set(44, 69.71)
        wltpMap.set(45, 70.08)
        wltpMap.set(46, 70.81)
        wltpMap.set(47, 71.17)
        wltpMap.set(48, 71.54)
        wltpMap.set(49, 72.27)
        wltpMap.set(50, 72.63)
        wltpMap.set(51, 73.00)
        wltpMap.set(52, 73.73)
        wltpMap.set(53, 74.09)
        wltpMap.set(54, 74.46)
        wltpMap.set(55, 75.19)
        wltpMap.set(56, 75.55)
        wltpMap.set(57, 76.28)
        wltpMap.set(58, 76.65)
        wltpMap.set(59, 77.38)
        wltpMap.set(60, 77.74)
        wltpMap.set(61, 78.47)
        wltpMap.set(62, 78.84)
        wltpMap.set(63, 79.57)
        wltpMap.set(64, 79.93)
        wltpMap.set(65, 80.66)
        wltpMap.set(66, 81.03)
        wltpMap.set(67, 81.76)
        wltpMap.set(68, 82.12)
        wltpMap.set(69, 82.85)
        wltpMap.set(70, 83.58)
        wltpMap.set(71, 83.95)
        wltpMap.set(72, 84.68)
        wltpMap.set(73, 85.04)
        wltpMap.set(74, 85.77)
        wltpMap.set(75, 86.50)
        wltpMap.set(76, 86.87)
        wltpMap.set(77, 87.60)
        wltpMap.set(78, 88.33)
        wltpMap.set(79, 89.06)
        wltpMap.set(80, 89.42)
        wltpMap.set(81, 90.15)
        wltpMap.set(82, 90.88)
        wltpMap.set(83, 91.61)
        wltpMap.set(84, 92.34)
        wltpMap.set(85, 93.07)
        wltpMap.set(86, 93.44)
        wltpMap.set(87, 94.17)
        wltpMap.set(88, 94.90)
        wltpMap.set(89, 95.63)
        wltpMap.set(90, 96.36)
        wltpMap.set(91, 97.09)
        wltpMap.set(92, 97.82)
        wltpMap.set(93, 98.55)
        wltpMap.set(94, 99.28)
        wltpMap.set(95, 100.01)
        wltpMap.set(96, 100.74)
        wltpMap.set(97, 101.47)
        wltpMap.set(98, 102.20)
        wltpMap.set(99, 102.93)
        wltpMap.set(100, 103.66)
        wltpMap.set(101, 104.39)
        wltpMap.set(102, 105.48)
        wltpMap.set(103, 106.21)
        wltpMap.set(104, 106.94)
        wltpMap.set(105, 107.67)
        wltpMap.set(106, 108.40)
        wltpMap.set(107, 109.50)
        wltpMap.set(108, 110.23)
        wltpMap.set(109, 110.96)
        wltpMap.set(110, 112.05)
        wltpMap.set(111, 112.78)
        wltpMap.set(112, 113.51)
        wltpMap.set(113, 114.61)
        wltpMap.set(114, 115.34)
        wltpMap.set(115, 116.07)
        wltpMap.set(116, 117.16)
        wltpMap.set(117, 117.89)
        wltpMap.set(118, 118.99)
        wltpMap.set(119, 119.72)
        wltpMap.set(120, 120.81)
        wltpMap.set(121, 121.54)
        wltpMap.set(122, 122.64)
        wltpMap.set(123, 123.37)
        wltpMap.set(124, 124.46)
        wltpMap.set(125, 125.56)
        wltpMap.set(126, 126.29)
        wltpMap.set(127, 127.38)
        wltpMap.set(128, 128.48)
        wltpMap.set(129, 129.21)
        wltpMap.set(130, 130.30)
        wltpMap.set(131, 131.40)
        wltpMap.set(132, 132.13)
        wltpMap.set(133, 133.22)
        wltpMap.set(134, 134.32)
        wltpMap.set(135, 135.41)
        wltpMap.set(136, 136.51)
        wltpMap.set(137, 137.60)
        wltpMap.set(138, 139.43)
        wltpMap.set(139, 140.89)
        wltpMap.set(140, 142.71)
        wltpMap.set(141, 144.54)
        wltpMap.set(142, 146.36)
        wltpMap.set(143, 147.82)
        wltpMap.set(144, 149.65)
        wltpMap.set(145, 151.47)
        wltpMap.set(146, 153.30)
        wltpMap.set(147, 155.12)
        wltpMap.set(148, 156.95)
        wltpMap.set(149, 158.77)
        wltpMap.set(150, 160.96)
        wltpMap.set(151, 162.79)
        wltpMap.set(152, 164.61)
        wltpMap.set(153, 166.80)
        wltpMap.set(154, 168.63)
        wltpMap.set(155, 170.45)
        wltpMap.set(156, 172.64)
        wltpMap.set(157, 174.83)
        wltpMap.set(158, 176.66)
        wltpMap.set(159, 178.85)
        wltpMap.set(160, 181.04)
        wltpMap.set(161, 183.23)
        wltpMap.set(162, 185.42)
        wltpMap.set(163, 187.61)
        wltpMap.set(164, 189.80)
        wltpMap.set(165, 191.99)
        wltpMap.set(166, 194.18)
        wltpMap.set(167, 196.73)
        wltpMap.set(168, 198.92)
        wltpMap.set(169, 201.11)
        wltpMap.set(170, 203.67)
        wltpMap.set(171, 205.86)
        wltpMap.set(172, 208.41)
        wltpMap.set(173, 210.97)
        wltpMap.set(174, 213.16)
        wltpMap.set(175, 215.71)
        wltpMap.set(176, 218.27)
        wltpMap.set(177, 220.82)
        wltpMap.set(178, 223.38)
        wltpMap.set(179, 225.93)
        wltpMap.set(180, 228.49)
        wltpMap.set(181, 231.04)
        wltpMap.set(182, 233.96)
        wltpMap.set(183, 236.52)
        wltpMap.set(184, 239.07)
        wltpMap.set(185, 241.99)
        wltpMap.set(186, 244.55)
        wltpMap.set(187, 247.47)
        wltpMap.set(188, 250.02)
        wltpMap.set(189, 252.94)
        wltpMap.set(190, 255.86)
        wltpMap.set(191, 258.78)
        wltpMap.set(192, 261.70)
        wltpMap.set(193, 264.62)
        wltpMap.set(194, 267.54)
        wltpMap.set(195, 270.46)
        wltpMap.set(196, 273.38)
        wltpMap.set(197, 276.30)
        wltpMap.set(198, 279.22)
        wltpMap.set(199, 282.51)
        wltpMap.set(200, 285.43)
        wltpMap.set(201, 288.71)
        wltpMap.set(202, 291.63)
        wltpMap.set(203, 294.92)
        wltpMap.set(204, 297.84)
        wltpMap.set(205, 301.12)
        wltpMap.set(206, 304.41)
        wltpMap.set(207, 307.69)
        wltpMap.set(208, 310.61)
        wltpMap.set(209, 313.90)
        wltpMap.set(210, 316.45)
        wltpMap.set(211, 319.01)
        wltpMap.set(212, 321.56)
        wltpMap.set(213, 324.12)
        wltpMap.set(214, 326.67)
        wltpMap.set(215, 329.23)
        wltpMap.set(216, 332.15)
        wltpMap.set(217, 334.70)
        wltpMap.set(218, 337.26)
        wltpMap.set(219, 339.81)
        wltpMap.set(220, 342.73)
        wltpMap.set(221, 345.29)
        wltpMap.set(222, 347.84)
        wltpMap.set(223, 350.76)
        wltpMap.set(224, 353.32)
        wltpMap.set(225, 355.87)
        wltpMap.set(226, 358.79)
        wltpMap.set(227, 361.35)
        wltpMap.set(228, 364.27)
        wltpMap.set(229, 366.82)
        wltpMap.set(230, 369.74)
        wltpMap.set(231, 372.30)
        wltpMap.set(232, 374.85)
        wltpMap.set(233, 377.77)
        wltpMap.set(234, 380.33)
        wltpMap.set(235, 383.25)
        wltpMap.set(236, 386.17)
        wltpMap.set(237, 388.72)
        wltpMap.set(238, 391.64)
        wltpMap.set(239, 394.20)
        wltpMap.set(240, 397.12)
        wltpMap.set(241, 399.67)
        wltpMap.set(242, 402.59)
        wltpMap.set(243, 405.15)
        wltpMap.set(244, 408.07)
        wltpMap.set(245, 410.62)
        wltpMap.set(246, 413.54)
        wltpMap.set(247, 416.10)
        wltpMap.set(248, 419.02)
        wltpMap.set(249, 421.57)
        wltpMap.set(250, 424.49)
        wltpMap.set(251, 427.41)
        wltpMap.set(252, 429.60)
        wltpMap.set(253, 431.79)
        wltpMap.set(254, 433.98)
        wltpMap.set(255, 436.17)
        wltpMap.set(256, 438.36)
        wltpMap.set(257, 440.55)
        wltpMap.set(258, 442.74)
        wltpMap.set(259, 444.93)
        wltpMap.set(260, 447.12)
        wltpMap.set(261, 449.31)
        wltpMap.set(262, 451.50)
        wltpMap.set(263, 453.69)
        wltpMap.set(264, 455.88)
        wltpMap.set(265, 457.71)
        wltpMap.set(266, 459.90)
        wltpMap.set(267, 462.09)
        wltpMap.set(268, 464.28)
        wltpMap.set(269, 466.47)
        wltpMap.set(270, 468.66)
        wltpMap.set(271, 470.48)
        wltpMap.set(272, 472.67)
        wltpMap.set(273, 474.86)
        wltpMap.set(274, 477.05)
        wltpMap.set(275, 478.88)
        wltpMap.set(276, 481.07)
        wltpMap.set(277, 483.26)
        wltpMap.set(278, 485.08)
        wltpMap.set(279, 487.27)
        wltpMap.set(280, 489.10)
        wltpMap.set(281, 491.29)
        wltpMap.set(282, 493.11)
        wltpMap.set(283, 495.30)
        wltpMap.set(284, 497.49)
        wltpMap.set(285, 499.32)
        wltpMap.set(286, 501.14)
        wltpMap.set(287, 503.33)
        wltpMap.set(288, 505.16)
        wltpMap.set(289, 507.35)
        wltpMap.set(290, 509.17)
        wltpMap.set(291, 511.00)
        wltpMap.set(292, 513.19)
        wltpMap.set(293, 515.01)
        wltpMap.set(294, 516.84)
        wltpMap.set(295, 518.66)
        wltpMap.set(296, 520.49)
        wltpMap.set(297, 522.68)
        wltpMap.set(298, 524.50)
        wltpMap.set(299, 526.33)
        wltpMap.set(300, 528.15)
        wltpMap.set(301, 529.98)
        wltpMap.set(302, 531.80)
        wltpMap.set(303, 533.63)
        wltpMap.set(304, 535.45)
        wltpMap.set(305, 537.28)
        wltpMap.set(306, 539.10)
        wltpMap.set(307, 540.56)
        wltpMap.set(308, 542.39)
        wltpMap.set(309, 544.21)
        wltpMap.set(310, 546.04)
        wltpMap.set(311, 547.86)
        wltpMap.set(312, 549.32)
        wltpMap.set(313, 551.15)
        wltpMap.set(314, 552.61)
        wltpMap.set(315, 554.43)
        wltpMap.set(316, 556.26)
        wltpMap.set(317, 557.72)
        wltpMap.set(318, 559.54)
        wltpMap.set(319, 561.00)
        wltpMap.set(320, 562.83)
        wltpMap.set(321, 564.29)
        wltpMap.set(322, 565.75)
        wltpMap.set(323, 567.57)
        wltpMap.set(324, 569.03)
        wltpMap.set(325, 570.49)
        wltpMap.set(326, 571.95)
        wltpMap.set(327, 573.78)
        wltpMap.set(328, 575.24)
        wltpMap.set(329, 576.70)
        wltpMap.set(330, 578.16)
        wltpMap.set(331, 579.62)
        wltpMap.set(332, 581.08)
        wltpMap.set(333, 582.54)
        wltpMap.set(334, 584.00)
        wltpMap.set(335, 585.46)
        wltpMap.set(336, 586.92)
        wltpMap.set(337, 588.38)
        wltpMap.set(338, 589.84)
        wltpMap.set(339, 590.93)
        wltpMap.set(340, 592.39)
        wltpMap.set(341, 593.85)
        wltpMap.set(342, 595.31)
        wltpMap.set(343, 596.41)
        wltpMap.set(344, 597.87)
        wltpMap.set(345, 599.33)
        wltpMap.set(346, 600.42)
        wltpMap.set(347, 601.88)
        wltpMap.set(348, 602.98)
        wltpMap.set(349, 604.44)
        wltpMap.set(350, 605.53)
        wltpMap.set(351, 606.99)
        wltpMap.set(352, 608.09)
        wltpMap.set(353, 609.18)
        wltpMap.set(354, 610.64)
        wltpMap.set(355, 611.74)
        wltpMap.set(356, 612.83)
        wltpMap.set(357, 613.93)
        wltpMap.set(358, 615.02)
        wltpMap.set(359, 616.48)
        wltpMap.set(360, 617.58)
        wltpMap.set(361, 618.67)
        wltpMap.set(362, 619.77)
        wltpMap.set(363, 620.86)
        wltpMap.set(364, 621.96)
        wltpMap.set(365, 623.05)
        wltpMap.set(366, 624.15)
        wltpMap.set(367, 625.24)
        wltpMap.set(368, 626.34)
        wltpMap.set(369, 627.43)
        wltpMap.set(370, 628.16)
        wltpMap.set(371, 629.26)
        wltpMap.set(372, 630.35)
        wltpMap.set(373, 631.45)
        wltpMap.set(374, 632.18)
        wltpMap.set(375, 633.27)
        wltpMap.set(376, 634.37)
        wltpMap.set(377, 635.10)
        wltpMap.set(378, 636.19)
        wltpMap.set(379, 636.92)
        wltpMap.set(380, 638.02)
        wltpMap.set(381, 639.11)
        wltpMap.set(382, 639.84)
        wltpMap.set(383, 640.57)
        wltpMap.set(384, 641.67)
        wltpMap.set(385, 642.40)
        wltpMap.set(386, 643.49)
        wltpMap.set(387, 644.22)
        wltpMap.set(388, 644.95)
        wltpMap.set(389, 646.05)
        wltpMap.set(390, 646.78)
        wltpMap.set(391, 647.51)
        wltpMap.set(392, 648.24)
        wltpMap.set(393, 649.33)
        wltpMap.set(394, 650.06)
        wltpMap.set(395, 650.79)
        wltpMap.set(396, 651.52)
        wltpMap.set(397, 652.25)
        wltpMap.set(398, 652.98)
        wltpMap.set(399, 653.71)
        wltpMap.set(400, 654.44)

        return wltpMap
    }

    static populateWltpMapPost2025(): Map<number, number> {
        const wltpMap: Map<number, number> = new Map()
        for (let i = 0; i <= 102; i++) {
            wltpMap.set(i, 106.21)
        }
        wltpMap.set(103, 106.21)
        wltpMap.set(104, 106.94)
        wltpMap.set(105, 107.67)
        wltpMap.set(106, 108.40)
        wltpMap.set(107, 109.50)
        wltpMap.set(108, 110.23)
        wltpMap.set(109, 110.96)
        wltpMap.set(110, 112.05)
        wltpMap.set(111, 112.78)
        wltpMap.set(112, 113.51)
        wltpMap.set(113, 114.61)
        wltpMap.set(114, 115.34)
        wltpMap.set(115, 116.07)
        wltpMap.set(116, 117.16)
        wltpMap.set(117, 117.89)
        wltpMap.set(118, 118.99)
        wltpMap.set(119, 119.72)
        wltpMap.set(120, 120.81)
        wltpMap.set(121, 121.54)
        wltpMap.set(122, 122.64)
        wltpMap.set(123, 123.37)
        wltpMap.set(124, 124.46)
        wltpMap.set(125, 125.56)
        wltpMap.set(126, 126.29)
        wltpMap.set(127, 127.38)
        wltpMap.set(128, 128.48)
        wltpMap.set(129, 129.21)
        wltpMap.set(130, 130.30)
        wltpMap.set(131, 131.40)
        wltpMap.set(132, 132.13)
        wltpMap.set(133, 133.22)
        wltpMap.set(134, 134.32)
        wltpMap.set(135, 135.41)
        wltpMap.set(136, 136.51)
        wltpMap.set(137, 137.60)
        wltpMap.set(138, 139.43)
        wltpMap.set(139, 140.89)
        wltpMap.set(140, 142.71)
        wltpMap.set(141, 144.54)
        wltpMap.set(142, 146.36)
        wltpMap.set(143, 147.82)
        wltpMap.set(144, 149.65)
        wltpMap.set(145, 151.47)
        wltpMap.set(146, 153.30)
        wltpMap.set(147, 155.12)
        wltpMap.set(148, 156.95)
        wltpMap.set(149, 158.77)
        wltpMap.set(150, 160.96)
        wltpMap.set(151, 162.79)
        wltpMap.set(152, 164.61)
        wltpMap.set(153, 166.80)
        wltpMap.set(154, 168.63)
        wltpMap.set(155, 170.45)
        wltpMap.set(156, 172.64)
        wltpMap.set(157, 174.83)
        wltpMap.set(158, 176.66)
        wltpMap.set(159, 178.85)
        wltpMap.set(160, 181.04)
        wltpMap.set(161, 183.23)
        wltpMap.set(162, 185.42)
        wltpMap.set(163, 187.61)
        wltpMap.set(164, 189.80)
        wltpMap.set(165, 191.99)
        wltpMap.set(166, 194.18)
        wltpMap.set(167, 196.73)
        wltpMap.set(168, 198.92)
        wltpMap.set(169, 201.11)
        wltpMap.set(170, 203.67)
        wltpMap.set(171, 205.86)
        wltpMap.set(172, 208.41)
        wltpMap.set(173, 210.97)
        wltpMap.set(174, 213.16)
        wltpMap.set(175, 215.71)
        wltpMap.set(176, 218.27)
        wltpMap.set(177, 220.82)
        wltpMap.set(178, 223.38)
        wltpMap.set(179, 225.93)
        wltpMap.set(180, 228.49)
        wltpMap.set(181, 231.04)
        wltpMap.set(182, 233.96)
        wltpMap.set(183, 236.52)
        wltpMap.set(184, 239.07)
        wltpMap.set(185, 241.99)
        wltpMap.set(186, 244.55)
        wltpMap.set(187, 247.47)
        wltpMap.set(188, 250.02)
        wltpMap.set(189, 252.94)
        wltpMap.set(190, 255.86)
        wltpMap.set(191, 258.78)
        wltpMap.set(192, 261.70)
        wltpMap.set(193, 264.62)
        wltpMap.set(194, 267.54)
        wltpMap.set(195, 270.46)
        wltpMap.set(196, 273.38)
        wltpMap.set(197, 276.30)
        wltpMap.set(198, 279.22)
        wltpMap.set(199, 282.51)
        wltpMap.set(200, 285.43)
        wltpMap.set(201, 288.71)
        wltpMap.set(202, 291.63)
        wltpMap.set(203, 294.92)
        wltpMap.set(204, 297.84)
        wltpMap.set(205, 301.12)
        wltpMap.set(206, 304.41)
        wltpMap.set(207, 307.69)
        wltpMap.set(208, 310.61)
        wltpMap.set(209, 313.90)
        wltpMap.set(210, 316.45)
        wltpMap.set(211, 319.01)
        wltpMap.set(212, 321.56)
        wltpMap.set(213, 324.12)
        wltpMap.set(214, 326.67)
        wltpMap.set(215, 329.23)
        wltpMap.set(216, 332.15)
        wltpMap.set(217, 334.70)
        wltpMap.set(218, 337.26)
        wltpMap.set(219, 339.81)
        wltpMap.set(220, 342.73)
        wltpMap.set(221, 345.29)
        wltpMap.set(222, 347.84)
        wltpMap.set(223, 350.76)
        wltpMap.set(224, 353.32)
        wltpMap.set(225, 355.87)
        wltpMap.set(226, 358.79)
        wltpMap.set(227, 361.35)
        wltpMap.set(228, 364.27)
        wltpMap.set(229, 366.82)
        wltpMap.set(230, 369.74)
        wltpMap.set(231, 372.30)
        wltpMap.set(232, 374.85)
        wltpMap.set(233, 377.77)
        wltpMap.set(234, 380.33)
        wltpMap.set(235, 383.25)
        wltpMap.set(236, 386.17)
        wltpMap.set(237, 388.72)
        wltpMap.set(238, 391.64)
        wltpMap.set(239, 394.20)
        wltpMap.set(240, 397.12)
        wltpMap.set(241, 399.67)
        wltpMap.set(242, 402.59)
        wltpMap.set(243, 405.15)
        wltpMap.set(244, 408.07)
        wltpMap.set(245, 410.62)
        wltpMap.set(246, 413.54)
        wltpMap.set(247, 416.10)
        wltpMap.set(248, 419.02)
        wltpMap.set(249, 421.57)
        wltpMap.set(250, 424.49)
        wltpMap.set(251, 427.41)
        wltpMap.set(252, 429.60)
        wltpMap.set(253, 431.79)
        wltpMap.set(254, 433.98)
        wltpMap.set(255, 436.17)
        wltpMap.set(256, 438.36)
        wltpMap.set(257, 440.55)
        wltpMap.set(258, 442.74)
        wltpMap.set(259, 444.93)
        wltpMap.set(260, 447.12)
        wltpMap.set(261, 449.31)
        wltpMap.set(262, 451.50)
        wltpMap.set(263, 453.69)
        wltpMap.set(264, 455.88)
        wltpMap.set(265, 457.71)
        wltpMap.set(266, 459.90)
        wltpMap.set(267, 462.09)
        wltpMap.set(268, 464.28)
        wltpMap.set(269, 466.47)
        wltpMap.set(270, 468.66)
        wltpMap.set(271, 470.48)
        wltpMap.set(272, 472.67)
        wltpMap.set(273, 474.86)
        wltpMap.set(274, 477.05)
        wltpMap.set(275, 478.88)
        wltpMap.set(276, 481.07)
        wltpMap.set(277, 483.26)
        wltpMap.set(278, 485.08)
        wltpMap.set(279, 487.27)
        wltpMap.set(280, 489.10)
        wltpMap.set(281, 491.29)
        wltpMap.set(282, 493.11)
        wltpMap.set(283, 495.30)
        wltpMap.set(284, 497.49)
        wltpMap.set(285, 499.32)
        wltpMap.set(286, 501.14)
        wltpMap.set(287, 503.33)
        wltpMap.set(288, 505.16)
        wltpMap.set(289, 507.35)
        wltpMap.set(290, 509.17)
        wltpMap.set(291, 511.00)
        wltpMap.set(292, 513.19)
        wltpMap.set(293, 515.01)
        wltpMap.set(294, 516.84)
        wltpMap.set(295, 518.66)
        wltpMap.set(296, 520.49)
        wltpMap.set(297, 522.68)
        wltpMap.set(298, 524.50)
        wltpMap.set(299, 526.33)
        wltpMap.set(300, 528.15)
        wltpMap.set(301, 529.98)
        wltpMap.set(302, 531.80)
        wltpMap.set(303, 533.63)
        wltpMap.set(304, 535.45)
        wltpMap.set(305, 537.28)
        wltpMap.set(306, 539.10)
        wltpMap.set(307, 540.56)
        wltpMap.set(308, 542.39)
        wltpMap.set(309, 544.21)
        wltpMap.set(310, 546.04)
        wltpMap.set(311, 547.86)
        wltpMap.set(312, 549.32)
        wltpMap.set(313, 551.15)
        wltpMap.set(314, 552.61)
        wltpMap.set(315, 554.43)
        wltpMap.set(316, 556.26)
        wltpMap.set(317, 557.72)
        wltpMap.set(318, 559.54)
        wltpMap.set(319, 561.00)
        wltpMap.set(320, 562.83)
        wltpMap.set(321, 564.29)
        wltpMap.set(322, 565.75)
        wltpMap.set(323, 567.57)
        wltpMap.set(324, 569.03)
        wltpMap.set(325, 570.49)
        wltpMap.set(326, 571.95)
        wltpMap.set(327, 573.78)
        wltpMap.set(328, 575.24)
        wltpMap.set(329, 576.70)
        wltpMap.set(330, 578.16)
        wltpMap.set(331, 579.62)
        wltpMap.set(332, 581.08)
        wltpMap.set(333, 582.54)
        wltpMap.set(334, 584.00)
        wltpMap.set(335, 585.46)
        wltpMap.set(336, 586.92)
        wltpMap.set(337, 588.38)
        wltpMap.set(338, 589.84)
        wltpMap.set(339, 590.93)
        wltpMap.set(340, 592.39)
        wltpMap.set(341, 593.85)
        wltpMap.set(342, 595.31)
        wltpMap.set(343, 596.41)
        wltpMap.set(344, 597.87)
        wltpMap.set(345, 599.33)
        wltpMap.set(346, 600.42)
        wltpMap.set(347, 601.88)
        wltpMap.set(348, 602.98)
        wltpMap.set(349, 604.44)
        wltpMap.set(350, 605.53)
        wltpMap.set(351, 606.99)
        wltpMap.set(352, 608.09)
        wltpMap.set(353, 609.18)
        wltpMap.set(354, 610.64)
        wltpMap.set(355, 611.74)
        wltpMap.set(356, 612.83)
        wltpMap.set(357, 613.93)
        wltpMap.set(358, 615.02)
        wltpMap.set(359, 616.48)
        wltpMap.set(360, 617.58)
        wltpMap.set(361, 618.67)
        wltpMap.set(362, 619.77)
        wltpMap.set(363, 620.86)
        wltpMap.set(364, 621.96)
        wltpMap.set(365, 623.05)
        wltpMap.set(366, 624.15)
        wltpMap.set(367, 625.24)
        wltpMap.set(368, 626.34)
        wltpMap.set(369, 627.43)
        wltpMap.set(370, 628.16)
        wltpMap.set(371, 629.26)
        wltpMap.set(372, 630.35)
        wltpMap.set(373, 631.45)
        wltpMap.set(374, 632.18)
        wltpMap.set(375, 633.27)
        wltpMap.set(376, 634.37)
        wltpMap.set(377, 635.10)
        wltpMap.set(378, 636.19)
        wltpMap.set(379, 636.92)
        wltpMap.set(380, 638.02)
        wltpMap.set(381, 639.11)
        wltpMap.set(382, 639.84)
        wltpMap.set(383, 640.57)
        wltpMap.set(384, 641.67)
        wltpMap.set(385, 642.40)
        wltpMap.set(386, 643.49)
        wltpMap.set(387, 644.22)
        wltpMap.set(388, 644.95)
        wltpMap.set(389, 646.05)
        wltpMap.set(390, 646.78)
        wltpMap.set(391, 647.51)
        wltpMap.set(392, 648.24)
        wltpMap.set(393, 649.33)
        wltpMap.set(394, 650.06)
        wltpMap.set(395, 650.79)
        wltpMap.set(396, 651.52)
        wltpMap.set(397, 652.25)
        wltpMap.set(398, 652.98)
        wltpMap.set(399, 653.71)
        wltpMap.set(400, 654.44)

        return wltpMap
    }
}
